import type { Middleware } from 'redux';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { configureSalesCheckinCustomerCard } from './config';

import { bootstrapActionTypes, initActionTypes } from '@makemydeal/dr-shared-store';
import { seedCustomerCard, customerCardSelectors, init, SAVE_PARTICIPANTS_SUCCESS } from '@makemydeal/dr-activities-customer-card';
import { composeShopper, mapCommonConsumerToShopper } from '@makemydeal/dr-sales-checkin-ui-utils';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';

import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import type { Participant } from '@makemydeal/dr-sales-checkin-types';
import {
    Actions,
    getEnableCorrectPhoneHandling,
    updateActiveDealFromParticipantsSave,
    type SalesCheckinRootState
} from '@makemydeal/dr-sales-checkin-store';

type DealStructureParticipants = { buyer?: Participant; coBuyer?: Participant };

/**
 * The participant/insurance half of the card seed, derived from a DealXG record. Shared by the init
 * and deal-refresh paths: the record is the same shape in both, only the response envelope differs
 * (`payload.deal.details` on init, `payload.data.deal` on a plain deal fetch). Returns null when the
 * deal carries no commonOrgId or no buyer — callers must treat that as "not seedable" and skip the
 * dispatch, since the card is unusable without them and a partial seed is worse than a stale one.
 */
const buildSeedFromDeal = (dealDetails: any, enableCorrectPhoneHandling: boolean) => {
    const commonOrgId: string | undefined = dealDetails?.coxAutoReferences?.commonOrgId;
    const participants: DealStructureParticipants | undefined = dealDetails?.dealStructure?.participants;
    const globalParticipants = dealDetails?.participants;

    if (!commonOrgId || !participants?.buyer) return null;

    const buyer = participants.buyer;
    const coBuyer = participants.coBuyer ?? null;
    const buyerGlobalParticipant = offerReduxUtils.getParticipant(buyer, globalParticipants, true) ?? null;
    const coBuyerGlobalParticipant = coBuyer ? offerReduxUtils.getParticipant(coBuyer, globalParticipants, true) ?? null : null;

    return {
        buyer: composeShopper(participants.buyer, commonOrgId, buyerGlobalParticipant, enableCorrectPhoneHandling),
        coBuyer: coBuyer ? composeShopper(coBuyer, commonOrgId, coBuyerGlobalParticipant, enableCorrectPhoneHandling) : null,
        hasCoBuyer: Boolean(coBuyer),
        dealId: dealDetails.dealId,
        // Normalised to null rather than left undefined so "the deal has no insurance" is stated
        // explicitly. Note this does NOT clear a previously seeded value on re-seed: the reducer does
        // `payload.vehicleInsuranceInfo ?? state.vehicleInsuranceInfo ?? null`, and `??` treats null as
        // nullish, so it falls back either way. Clearing would need the reducer to switch to the
        // `!== undefined` check it already uses for coBuyer — a change in the shared activity lib.
        vehicleInsuranceInfo: dealDetails.vehicleInsurance ?? null,
        commonOrgId
    };
};

// The activity owns the SPD lifecycle: its own middleware watches SEED_CUSTOMER_CARD and, when the
// seed carries prefetchSpd (default on), fires the SPD presence CHECK itself. Registered as its own
// chain entry in remainingMiddleware.ts so the CHECK response can seed the activitiesCustomerSpd slice.
export const customerCardActivityMiddleware = init().middlewareMgr.middleware;

export const customerCardMiddleware: Middleware<unknown, SalesCheckinRootState> = (store) => (next) => (action: AnyFSA) => {
    // Let the reducers process the action first. INIT_PENCIL_SUCCESS is what flips the shared bootstrap
    // loadingState to LoadedSuccessfully, so seeding (and the activity's downstream feature-toggle guard)
    // must run after next(action) — otherwise wasBootstrapSuccessful is still false and the toggle
    // selectors throw. Mirrors the "reducers execute first" pattern in postBootstrapMiddleware.
    const result = next(action);

    switch (action.type) {
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            configureSalesCheckinCustomerCard(store.getState());
            break;
        }
        case initActionTypes.INIT_PENCIL_SUCCESS: {
            const dealerMMDId: number | undefined = action.payload?.dealerDetails?.dealerSettings?.dealerMMDId;
            const seed = buildSeedFromDeal(action.payload?.deal?.details, getEnableCorrectPhoneHandling(action));
            if (seed) {
                store.dispatch(
                    seedCustomerCard({
                        ...seed,
                        initialTab: 'buyer',
                        dealerId: dealerMMDId
                    })
                );
            }
            break;
        }
        // GTL deal refresh (DealRefreshModal → fetchDeal) and every other plain deal fetch. The
        // response carries the whole DealXG, so re-seed buyer/co-buyer/insurance from it — otherwise
        // the card keeps rendering the pre-refresh participants while activeDeal.details is fresh.
        // Two differences from init: the record sits at `data.deal` with no `.details` wrapper, and
        // the response has no dealer block, so dealerId comes from what init already seeded.
        // initialTab and isVehicleInsuranceUpdated are omitted so the reducer preserves them.
        //
        // prefetchSpd stays ON here, unlike the co-buyer path below. A deal refresh is exactly when
        // the server is the authority, and the CHECK is the only thing that re-asks once presence
        // exists — CustomerSpd's own self-heal fires on *missing* presence, not stale. The CHECK is
        // what refreshes the presence flags, canDesk, and driversLicenseState (a non-sensitive code
        // the CHECK carries by value), and it targets the post-refresh CCIDs, which matters when the
        // deal's participants changed. Known gap: it does NOT refresh already-revealed PII —
        // getSpdStatus stays 'success' and blocks the GET until resetCustomerSpdLoadState() runs,
        // which the activity does not currently export to hosts.
        case Actions.FETCH_DEAL_SUCCESS: {
            const seed = buildSeedFromDeal(action.payload?.data?.deal, getEnableCorrectPhoneHandling(action));

            if (seed) {
                const current = customerCardSelectors.getCustomerCardState(store.getState() as never);

                store.dispatch(
                    seedCustomerCard({
                        ...seed,
                        dealerId: current?.dealerId,
                        prefetchSpd: true
                    })
                );
            }
            break;
        }
        // The save's own path reaches only `customer.checkedInCustomer`, so folding its deal in is what
        // refreshes the participants everything reading `activeDeal.details` renders.
        case SAVE_PARTICIPANTS_SUCCESS: {
            const { dxgDeal, applicationState } = action.payload?.data ?? {};

            if (dxgDeal) {
                store.dispatch(updateActiveDealFromParticipantsSave(dxgDeal, applicationState));
            }
            break;
        }
        // Mid-session co-buyer change: sync the co-buyer into the activity slice WITHOUT re-seeding from
        // the pencil. A null payload clears the co-buyer; a present one sets it. We preserve the existing
        // buyer/insurance/dealId and pass prefetchSpd:false so the SPD presence CHECK does not re-fire and
        // wipe credit-app/SPD data.
        case Actions.SET_CO_BUYER:
        case Actions.REMOVE_CO_BUYER: {
            const current = customerCardSelectors.getCustomerCardState(store.getState() as never);
            const coBuyer = (action.payload?.coBuyer as CommonConsumer | undefined) ?? null;
            const hasCoBuyer = Boolean(coBuyer);

            if (current?.buyer) {
                store.dispatch(
                    seedCustomerCard({
                        buyer: current.buyer,
                        coBuyer: coBuyer ? mapCommonConsumerToShopper(coBuyer, 'CoBuyer') : null,
                        hasCoBuyer,
                        initialTab: current.initialTab,
                        dealId: current.dealId,
                        dealerId: current.dealerId,
                        vehicleInsuranceInfo: current.vehicleInsuranceInfo,
                        isVehicleInsuranceUpdated: current.isVehicleInsuranceUpdated,
                        prefetchSpd: false
                    })
                );
            }
            break;
        }
    }

    return result;
};
